import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { gql } from '@apollo/client';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import getAccommodationType from '../../constants/getAccommodationType';
import searchParams from '../../constants/searchParams';
import { useTenantContext } from '../../context/TenantContext';
import { addApolloState, initApolloClient } from '../../createApolloClient';
import { Locale } from '../../entities/Locale';
import NotFoundError from '../../entities/NotFoundError';
import {
    AccommodationDetailsDocument,
    AccommodationDetailsQuery,
    AccommodationDetailsQueryVariables,
    useAccommodationDetailsQuery,
} from '../../generated/graphql';
import useAnalyticsAccoView from '../../hooks/analytics/useAnalyticsAccoView';
import useAnalyticsContentCategory from '../../hooks/analytics/useAnalyticsContentCategory';
import useQueryParams from '../../hooks/useQueryParams';
import createPlaceholder from '../../utils/createPlaceholder';
import getDefaultAccommodationType from '../../utils/getDefaultAccommodationType';
import { groupImageByCategoryClean } from '../../utils/groupImagesByCategory';
import setCanonicalLink from '../../utils/setCanonicalLink';
import { pickBookingFromParams } from '../../utils/trip';
import ContentWrapper from '../ContentWrapper';
import Hero, { HeroContainer, HeroPhotosButton } from '../Hero';
import PhotoModal from '../designsystem/PhotoModal';
import Section from '../designsystem/Section';
import HeroSearchBar from '../HeroSearchBar';
import Intro from '../Intro';
import LoadingDetail from '../LoadingDetail';
import Location from '../Location';
import Page from '../Page';
import SearchRentalUnits from '../SearchRentalUnits';
import Stack from '../Stack';
import UnitAttributes from '../UnitAttributes';
import UnitPhotos from '../UnitPhotos';
import UspsContent from '../UspsContent';
import { decodeQueryParams, StringParam } from 'use-query-params';
import { parse } from 'query-string';
import Head from 'next/head';

export const pageQuery = gql`
    query AccommodationDetails($slug: String!) {
        accommodation(slug: $slug) {
            id
            slug
            jpegHero: mainImage {
                transform(config: HERO, format: JPEG) {
                    ...Transform
                }
            }
            webpHero: mainImage {
                transform(config: HERO, format: WEBP) {
                    ...Transform
                }
            }
            type
            name
            hasPublicPage
            place
            descriptionGeneral
            descriptionLocation

            images {
                ...Images
            }

            attributeCategories {
                ...Extras
            }

            ...Location
        }
    }
`;

const AccommodationDetail: FC<React.PropsWithChildren<unknown>> = () => {
    const { query: routerQuery } = useRouter();
    const intl = useIntl();
    const [params] = useQueryParams({ ...searchParams, searched: StringParam });
    const searchedParams = params.searched ? decodeQueryParams(searchParams, parse(params.searched as string)) : null;
    const { open, onClose, onOpen } = useSesame();
    const [startIndex, setStartIndex] = useState(0);
    useAnalyticsContentCategory('accommodation');
    const { brandConfig } = useTenantContext();
    const { data, loading } = useAccommodationDetailsQuery({
        variables: {
            slug: routerQuery.slug as string,
        },
    });

    useAnalyticsAccoView(data?.accommodation);

    if (loading) {
        return <LoadingDetail withHero />;
    }

    if (!data?.accommodation || !data.accommodation.hasPublicPage) {
        throw new NotFoundError();
    }

    const {
        accommodation: {
            name,
            jpegHero,
            webpHero,
            place,
            descriptionGeneral,
            images,
            attributeCategories,
            coordinates,
            descriptionLocation,
            pointsOfInterest,
            type,
        },
    } = data;

    return (
        <>
            <Head>
                <title>{`${name} - ${brandConfig.seo.siteName}`}</title>
                {descriptionGeneral && <meta name="description" content={descriptionGeneral} />}
                <meta property="og:title" content={name} />
                {descriptionGeneral && <meta property="og:description" content={descriptionGeneral} />}
                <meta property="og:image" content={jpegHero?.transform?.src} />
                <meta property="og:image:alt" content={name} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={name} />
                {descriptionGeneral && <meta name="twitter:description" content={descriptionGeneral} />}
                <meta name="twitter:image" content={jpegHero?.transform?.src} />
                <meta name="twitter:image:alt" content={name} />
            </Head>
            <Page>
                <HeroContainer>
                    <HeroSearchBar
                        hasHero={!!(webpHero && jpegHero)}
                        initialValues={{
                            accommodationType: params.accommodationType ?? getDefaultAccommodationType(brandConfig),
                            booking: searchedParams ?? pickBookingFromParams(params),
                        }}
                    />
                    {jpegHero && webpHero && (
                        <Hero
                            ratio={9 / 16}
                            placeholder={createPlaceholder(webpHero.transform?.placeholder)}
                            sources={[
                                {
                                    srcSet: webpHero.transform?.srcSet,
                                    type: 'image/webp',
                                },
                                {
                                    srcSet: jpegHero.transform?.srcSet,
                                    type: 'image/jpeg',
                                },
                            ]}
                            fallback={{
                                src: jpegHero.transform?.src,
                            }}
                        >
                            {images.length > 0 && <HeroPhotosButton onClick={onOpen}>{images.length}</HeroPhotosButton>}
                        </Hero>
                    )}
                </HeroContainer>
                <Section>
                    <ContentWrapper style={{ paddingBottom: 0 }}>
                        <Stack spacing={[6, null, 0]}>
                            <Intro
                                title={place}
                                description={descriptionGeneral ?? undefined}
                                tagline={name}
                                withMarker
                            />
                            <SearchRentalUnits slug={routerQuery.slug as string} />
                        </Stack>
                    </ContentWrapper>
                </Section>
                <Section>
                    <ContentWrapper>
                        {images.length > 0 && (
                            <>
                                <UnitPhotos
                                    title={<FormattedMessage defaultMessage="Foto's" />}
                                    data={images}
                                    onCategoryClick={categoryIndex => {
                                        onOpen();
                                        setStartIndex(categoryIndex);
                                    }}
                                    onOpenPhotos={onOpen}
                                />
                                <PhotoModal
                                    imagesOrCategories={groupImageByCategoryClean(images, true)}
                                    open={open}
                                    onClose={onClose}
                                    startIndex={startIndex}
                                />
                            </>
                        )}
                    </ContentWrapper>
                    <UnitAttributes name={getAccommodationType(intl, type)} data={attributeCategories} />
                </Section>
                <Section>
                    <Location
                        data={{
                            coordinates,
                            descriptionLocation,
                            pointsOfInterest,
                        }}
                    />
                </Section>
            </Page>
            <UspsContent />
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, req, res, locale }) => {
    setCanonicalLink(req, res);
    const client = initApolloClient(locale as Locale);
    const { data } = await client.query<AccommodationDetailsQuery, AccommodationDetailsQueryVariables>({
        query: AccommodationDetailsDocument,
        variables: {
            slug: params?.slug as string,
        },
    });

    if (!data?.accommodation || !data.accommodation.hasPublicPage) {
        return {
            notFound: true,
        };
    }

    return addApolloState(client, { props: {} });
};

export default AccommodationDetail;
